import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { BOARD_SIZE, CELL_COUNT, type ClientCell, type Team } from "@msf/shared";
import { Cell } from "./Cell.js";

interface BoardProps {
  cells: ClientCell[];
  justRevealed: Set<number>;
  pendingCell: number | null;
  /** The most recent move's origin cell (tap or bomb centre) — highlighted so
   * either player can see where the last move happened, distinct from the
   * roving keyboard-focus cursor below. Null before any move this session. */
  lastMoveIndex: number | null;
  lastMoveTeam: Team | null;
  interactive: boolean;
  onTap: (x: number, y: number) => void;
  /** Rendered at the start/end of the control strip below the board — e.g. a
   * leave button and the chat toggle — so every secondary action lives in one
   * in-flow row that can never overlap the board itself. */
  leadingControl?: ReactNode;
  trailingControl?: ReactNode;
}

export function Board({
  cells,
  justRevealed,
  pendingCell,
  lastMoveIndex,
  lastMoveTeam,
  interactive,
  onTap,
  leadingControl,
  trailingControl,
}: BoardProps) {
  // Roving-tabindex cursor for keyboard play.
  const [cursor, setCursor] = useState(0);
  const gridRef = useRef<HTMLDivElement>(null);

  // Move DOM focus to the cursor cell (so a screen reader announces it).
  const focusCell = useCallback((i: number) => {
    const el = gridRef.current?.querySelectorAll<HTMLElement>("[data-cell]")[i];
    el?.focus();
    el?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, []);

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      const s = BOARD_SIZE;
      let next = cursor;
      switch (e.key) {
        case "ArrowRight":
          next = cursor % s < s - 1 ? cursor + 1 : cursor;
          break;
        case "ArrowLeft":
          next = cursor % s > 0 ? cursor - 1 : cursor;
          break;
        case "ArrowDown":
          next = cursor + s < CELL_COUNT ? cursor + s : cursor;
          break;
        case "ArrowUp":
          next = cursor - s >= 0 ? cursor - s : cursor;
          break;
        case "Home":
          next = cursor - (cursor % s);
          break;
        case "End":
          next = cursor - (cursor % s) + (s - 1);
          break;
        default:
          return; // Enter/Space are handled natively by the focused tile button.
      }
      e.preventDefault();
      if (next !== cursor) {
        setCursor(next);
        requestAnimationFrame(() => focusCell(next));
      }
    },
    [cursor, focusCell],
  );

  const grid = useMemo(
    () => (
      <div
        ref={gridRef}
        role="grid"
        aria-label="Minesweeper board — arrow keys to move, Enter to reveal"
        onKeyDown={onKeyDown}
        className="grid gap-[3px]"
        style={{ gridTemplateColumns: `repeat(${BOARD_SIZE}, minmax(0, 1fr))` }}
      >
        {cells.map((cell, i) => {
          const x = i % BOARD_SIZE;
          const y = Math.floor(i / BOARD_SIZE);
          return (
            <Cell
              key={i}
              cell={cell}
              x={x}
              y={y}
              justRevealed={justRevealed.has(i)}
              pending={i === pendingCell}
              lastMove={i === lastMoveIndex}
              lastMoveTeam={lastMoveTeam}
              focused={i === cursor}
              onTap={interactive && pendingCell === null ? onTap : () => {}}
            />
          );
        })}
      </div>
    ),
    [cells, justRevealed, pendingCell, lastMoveIndex, lastMoveTeam, interactive, onTap, cursor, onKeyDown],
  );

  return (
    <div className="w-full">
      <div
        className="clay-inset mx-auto w-full p-[6px]"
        style={{ maxWidth: "min(94vw, 30rem)", touchAction: "manipulation" }}
      >
        {grid}
      </div>

      {/* Secondary controls sit in one in-flow strip below the board so none of
          them (leave / chat) can ever overlap a tappable cell. */}
      <div
        className="mx-auto mt-2 flex items-center justify-between gap-2"
        style={{ maxWidth: "min(94vw, 30rem)" }}
      >
        <div>{leadingControl}</div>
        <div>{trailingControl}</div>
      </div>
    </div>
  );
}
